import { useCallback, useEffect, useRef } from "react";
// Returns a stable function that delays invocation of `fn` by `delayMs`
// since its last call. Used by the editor to autosave only after the
// user pauses typing, instead of hitting the API on every keystroke.
export function useDebouncedCallback(fn, delayMs) {
    const timerRef = useRef(null);
    const argsRef = useRef(null);
    const fnRef = useRef(fn);
    useEffect(() => {
        fnRef.current = fn;
    }, [fn]);
    const cancel = useCallback(() => {
        if (timerRef.current !== null) {
            window.clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        argsRef.current = null;
    }, []);
    const flush = useCallback(() => {
        if (timerRef.current !== null && argsRef.current) {
            window.clearTimeout(timerRef.current);
            const args = argsRef.current;
            timerRef.current = null;
            argsRef.current = null;
            void fnRef.current(...args);
        }
    }, []);
    const call = useCallback((...args) => {
        argsRef.current = args;
        if (timerRef.current !== null)
            window.clearTimeout(timerRef.current);
        timerRef.current = window.setTimeout(() => {
            timerRef.current = null;
            const a = argsRef.current;
            argsRef.current = null;
            void fnRef.current(...a);
        }, delayMs);
    }, [delayMs]);
    useEffect(() => () => cancel(), [cancel]);
    return { call, flush, cancel };
}
